import { Link } from "react-router-dom";
import type { PartFailure } from "../api";
import { useT } from "../i18n";
import { excerpt } from "./passage";

/**
 * FailedPassage is a passage with no audio, shown the way an author can act
 * on it (ADR-0120): the words themselves — never "Part 26" — with the ones the
 * check did not hear highlighted, why in plain words, and a way to the text.
 *
 * The same item on the chapter page and on the book's Needs attention list, so
 * the two never describe one failure two ways.
 */
export default function FailedPassage({
  chapterID,
  partID,
  text,
  failure,
  unsaidStart,
  unsaidEnd,
  retryable,
  message,
}: {
  chapterID: string;
  partID: string;
  text: string;
  failure: PartFailure;
  unsaidStart?: number;
  unsaidEnd?: number;
  retryable: boolean;
  message?: string;
}) {
  const t = useT();
  const { before, unsaid, after } = excerpt(text, unsaidStart, unsaidEnd);
  return (
    <li data-testid="failed-passage" data-failure={failure} className="text-sm">
      {/* The passage is authored text in the book's own language, and the
          block is the one place that carries dir="auto": nothing inside it
          sets a direction, or the outer element finds no strong character
          (CLAUDE.md). */}
      <blockquote
        dir="auto"
        className="border-s-2 border-amber-400 ps-3 text-start leading-relaxed text-ink-soft"
      >
        {before}
        {unsaid && (
          <mark
            data-testid="unsaid"
            className="rounded-sm bg-amber-100 px-0.5 text-ink dark:bg-amber-900/60"
          >
            {unsaid}
          </mark>
        )}
        {after}
      </blockquote>
      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
        {failure === "unsaid_run" && !unsaid
          ? t.narration.problemSkippedSomewhere
          : (t.narration.problemCause[failure] ?? t.narration.problemCause.unknown)}
      </p>
      {!retryable && t.narration.problemNoRetry[failure] && (
        <p className="mt-0.5 text-xs text-muted">{t.narration.problemNoRetry[failure]}</p>
      )}
      <div className="mt-1.5 flex flex-wrap items-start gap-3">
        <Link
          to={`/studio/chapters/${chapterID}?passage=${partID}`}
          data-testid="show-in-text"
          className="rounded-control border border-line px-2.5 py-1 text-xs text-ink transition-colors hover:bg-sunk"
        >
          {t.narration.problemShowInText}
        </Link>
        {message && (
          /* The pipeline's own sentence: a request id is what a provider
             asks for, so it stays one tap away, and small. */
          <details className="min-w-0 text-xs text-muted">
            <summary data-testid="failed-passage-details" className="cursor-pointer select-none">
              {t.narration.problemDetails}
            </summary>
            <p data-testid="failed-passage-message" className="mt-1 break-words">
              <bdi>{message}</bdi>
            </p>
          </details>
        )}
      </div>
    </li>
  );
}
